import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MediaCard from "@/components/feature/MediaCard";
import SectionHeading from "@/components/feature/SectionHeading";
import HeroCarousel, { type HeroSlideItem } from "@/pages/home/components/HeroCarousel";
import CategoryEntry from "@/pages/home/components/CategoryEntry";
import SpecialsSection from "@/pages/home/components/SpecialsSection";
import { allMedia, heroSlideData, hotMedia, latestMedia, categoryMeta } from "@/lib/media";

const highlights = [
  { icon: "ri-hd-line", title: "高清画质", desc: "精选高清片源，观影细节更清晰" },
  { icon: "ri-apps-2-line", title: "分类清晰", desc: "电影 / 剧集 / 综艺，多维筛选" },
  { icon: "ri-search-eye-line", title: "快速检索", desc: "关键词模糊搜索，秒找想看内容" },
  { icon: "ri-calendar-check-line", title: "持续更新", desc: "最新上线栏目，追踪热播新片" },
];

export default function Home() {
  const slides: HeroSlideItem[] = heroSlideData
    .map((slide) => {
      const media = allMedia.find((item) => item.id === slide.mediaId);
      if (!media) return null;
      return {
        id: media.id,
        title: media.title,
        slogan: slide.slogan,
        backdrop: slide.backdrop,
        summary: media.summary,
        genre: media.genre,
        region: media.region,
        year: media.year,
        rating: media.rating,
        duration: media.duration,
        categoryPath: `/category/${media.category}`,
        badge: media.badge,
      };
    })
    .filter((item): item is HeroSlideItem => item !== null);

  const latest = latestMedia(allMedia, 12);
  const hot = hotMedia(allMedia, 6);

  return (
    <SiteLayout overlayNav>
      {slides.length > 0 ? (
        <HeroCarousel slides={slides} />
      ) : (
        <div className="h-[560px] flex items-center justify-center text-foreground-500">
          暂无推荐内容
        </div>
      )}

      <div className="w-full px-4 md:px-6 py-12 md:py-16 space-y-14 md:space-y-20">
        <section>
          <SectionHeading
            eyebrow="快速进入"
            title="影视分类"
            subtitle="按类别浏览海量影视资源，快速定位你感兴趣的领域。"
          />
          <CategoryEntry />
        </section>

        <section>
          <SectionHeading
            eyebrow="近期更新"
            title="最新上线"
            subtitle="刚刚上架的高清影视作品，第一时间追上最新内容。"
            to="/category/movie"
          />
          {latest.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {latest.map((media) => (
                <MediaCard key={media.id} media={media} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-foreground-500">暂无推荐内容</p>
          )}
        </section>

        <section>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {highlights.map((item) => (
              <div
                key={item.title}
                className="p-5 rounded-lg bg-background-100 border border-background-200/70"
              >
                <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-2xl`} />
                </span>
                <h3 className="font-heading font-bold text-base text-foreground-950">{item.title}</h3>
                <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <SectionHeading
            eyebrow="人气推荐"
            title="热门影视推荐"
            subtitle="根据播放热度与用户关注度整理的必看片单。"
            to="/category/movie"
          />
          {hot.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {hot.map((media, i) => (
                <MediaCard key={media.id} media={media} rank={i + 1} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-foreground-500">暂无推荐内容</p>
          )}
        </section>

        <section>
          <SectionHeading
            eyebrow="主编策划"
            title="影视专题"
            subtitle="围绕主题精选的合辑，带你系统化地看完一类好片。"
            to="/special/s1"
            linkLabel="进入专题"
          />
          <SpecialsSection />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          <div className="lg:col-span-2 p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/70">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-400 mb-3">
              <i className="ri-film-line" />
              关于平台
            </span>
            <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950">
              91香蕉视频 · 丰富影视资源与高清观影体验
            </h2>
            <p className="mt-4 text-sm text-foreground-500 leading-relaxed">
              我们聚合电影、电视剧、综艺等多元影视内容，以简洁大气的门户式布局与灵活的分类筛选，
              帮助用户快速查找、浏览与收藏喜欢的作品。平台持续更新最新上线内容，并围绕热点推出编辑精选专题，
              让每一次打开都值得期待。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解我们
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-background-200/60 border border-background-300/60 text-foreground-900 text-sm font-semibold hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                联系我们
              </Link>
            </div>
          </div>
          <div className="p-6 md:p-8 rounded-lg bg-primary-500 text-background-50 flex flex-col justify-between">
            <div>
              <i className="ri-search-2-line text-4xl" />
              <h3 className="mt-4 font-heading font-extrabold text-xl">没找到想看的？</h3>
              <p className="mt-2 text-sm text-background-50/85 leading-relaxed">
                使用关键词搜索，快速定位电影、剧集或综艺，支持片名、类型、导演与标签模糊匹配。
              </p>
            </div>
            <Link
              to="/search"
              className="mt-6 inline-flex items-center gap-2 self-start px-4 py-2.5 rounded-md bg-background-50 text-primary-700 text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-search-line" />
              立即搜索
            </Link>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}